// frontend/src/context/AuthContext.tsx
import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  getAdditionalUserInfo
} from 'firebase/auth';
import { auth, googleProvider, twitterProvider } from '../config/firebase';
import { fetchUserStatus, setImpersonateUid, claimSharedPool } from '../api/api';
import { trackEvent, trackRedditEvent } from '../utils/analytics';

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  isPro: boolean;
  isSuperuser: boolean;
  twitterHandle: string | null;
  impersonatingUid: string | null;
  impersonatingEmail: string | null;
  setImpersonatingUid: (uid: string | null) => void;
  refreshProStatus: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithTwitter: () => Promise<{ handle: string | null; email: string | null }>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  getAuthToken: () => Promise<string | null>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{children: ReactNode}> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isPro, setIsPro] = useState<boolean>(false);
  const [isSuperuser, setIsSuperuser] = useState<boolean>(false);
  const [twitterHandle, setTwitterHandle] = useState<string | null>(null);
  const [impersonatingUid, setImpersonatingUidState] = useState<string | null>(null);
  const [impersonatingEmail, setImpersonatingEmail] = useState<string | null>(null);

  const setImpersonatingUid = useCallback((uid: string | null) => {
    setImpersonatingUidState(uid);
    setImpersonateUid(uid);
    if (!uid) setImpersonatingEmail(null);
  }, []);

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  // Sign in with Twitter/X — returns handle and email from the Twitter profile
  const signInWithTwitter = async (): Promise<{ handle: string | null; email: string | null }> => {
    try {
      const result = await signInWithPopup(auth, twitterProvider);
      const additionalInfo = getAdditionalUserInfo(result);
      const handle = additionalInfo?.username || null;
      if (handle) {
        setTwitterHandle(`@${handle}`);
      }

      // Extract email from Twitter profile (available if app has email permission)
      const twitterEmail = (additionalInfo?.profile as any)?.email || result.user?.email || null;

      return {
        handle: handle ? `@${handle}` : null,
        email: twitterEmail,
      };
    } catch (error) {
      console.error('Error signing in with Twitter:', error);
      throw error;
    }
  };

  // Sign in with email and password
  const signInWithEmail = async (email: string, password: string) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing in with email:', error);
      throw error;
    }
  };

  // Sign up with email and password
  const signUpWithEmail = async (email: string, password: string) => {
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error) {
      console.error('Error signing up with email:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      setImpersonatingUid(null);
      setIsSuperuser(false);
      await signOut(auth);
      localStorage.removeItem('bracketsim_auth');
    } catch (error) {
      console.error('Error logging out:', error);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Error resetting password:', error);
      throw error;
    }
  };

  // Add this new function to get the auth token
  const getAuthToken = async (): Promise<string | null> => {
    try {
      if (!currentUser) {
        return null;
      }
      return await currentUser.getIdToken(true);
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  };

  // Fetch pro status from backend
  const refreshProStatus = useCallback(async () => {
    try {
      const status = await fetchUserStatus();
      setIsPro(status.isPro);
      setIsSuperuser(status.isSuperuser || false);
      if (status.twitterHandle) {
        setTwitterHandle(status.twitterHandle);
      }
      if (status.email !== undefined) {
        setImpersonatingEmail(status.email || null);
      }
    } catch {
      setIsPro(false);
    }
  }, []);

  // During react-snap prerendering, skip auth entirely
  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') {
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        localStorage.setItem('bracketsim_auth', '1');
        // Fetch pro status once user is authenticated
        try {
          const token = await user.getIdToken(true);
          if (token) {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 10000);
            const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/user-status`, {
              headers: { 'Authorization': `Bearer ${token}` },
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (response.ok) {
              const data = await response.json();
              setIsPro(data.isPro);
              setIsSuperuser(data.isSuperuser || false);
              if (data.twitterHandle) {
                setTwitterHandle(data.twitterHandle);
              }
            }
          }
        } catch {
          setIsPro(false);
        }

        // Auto-claim shared pool if pending from a share link signup
        try {
          const pendingCode = localStorage.getItem('pendingShareCode');
          if (pendingCode) {
            localStorage.removeItem('pendingShareCode');
            await claimSharedPool(pendingCode);
          }
        } catch (err) {
          console.error('Failed to claim shared pool:', err);
        }
      } else {
        localStorage.removeItem('bracketsim_auth');
        setIsPro(false);
        setIsSuperuser(false);
        setTwitterHandle(null);
      }
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);

  // Check for checkout success param and refresh pro status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success' && currentUser) {
      trackEvent('purchase');
      trackRedditEvent('Purchase');
      refreshProStatus();
      // Clean up the URL param
      params.delete('checkout');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [currentUser, refreshProStatus]);

  // Refresh pro status when impersonation changes
  useEffect(() => {
    if (currentUser) {
      refreshProStatus();
    }
  }, [impersonatingUid, currentUser, refreshProStatus]);

  // Refresh pro status when tab regains focus (picks up purchases completed in other tabs)
  useEffect(() => {
    if (!currentUser) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshProStatus();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentUser, refreshProStatus]);

  // Expose the function to window object
  useEffect(() => {
    // Make the token available to the extension
    window.getFirebaseAuthToken = async () => {
      return await getAuthToken();
    };
    
    // Cleanup on component unmount
    return () => {
      // @ts-ignore - Remove the function when component unmounts
      window.getFirebaseAuthToken = undefined;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]); // Re-establish when user changes

  const value: AuthContextType = {
    currentUser,
    loading,
    isPro,
    isSuperuser,
    twitterHandle,
    impersonatingUid,
    impersonatingEmail,
    setImpersonatingUid,
    refreshProStatus,
    signInWithGoogle,
    signInWithTwitter,
    signInWithEmail,
    signUpWithEmail,
    logout,
    resetPassword,
    getAuthToken
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="min-h-screen bg-background flex flex-col items-center justify-center">
          <img
            src="/bracketsim_header.png"
            alt="My Bracket Sim"
            className="h-16 mb-6 opacity-80 dark:hidden"
          />
          <img
            src="/bracketsim_header_dark.png"
            alt="My Bracket Sim"
            className="h-16 mb-6 opacity-80 hidden dark:block"
          />
          <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Add this type declaration to make TypeScript happy with the window object extension
declare global {
  interface Window {
    getFirebaseAuthToken?: () => Promise<string | null>;
  }
}